import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { collectHistoricalStatusData, historicalStatusDataKey } from "./collectHistoricalStatusData";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { QueryDiagnosticDeviceData } from "@/models/QueryDiagnosticDeviceData";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

type StatusDataClient = Parameters<typeof fetchStatusDataComplete>[0];

/**
 * Key and fetcher of one historical status query, shared by {@link useHistoricalStatusData} and
 * callers that run one query per device (so adding a vehicle does not refetch the others).
 */
export function historicalStatusDataQuery(
  geotabClient: StatusDataClient,
  activeSessionId: string | null | undefined,
  diagnosticIds: string[],
  deviceIds: string[],
  fromDate: Date,
  toDate?: Date,
  interval?: number | false,
) {
  return {
    // The requested window is part of the identity of this query. Without it, moving the date
    // range served the previously cached range instead of refetching.
    queryKey: [
      "historicalStatusData",
      activeSessionId,
      diagnosticIds,
      deviceIds,
      fromDate.getTime(),
      toDate?.getTime() ?? null,
    ],

    queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<QueryDiagnosticDeviceData> => {
      // One Get per (diagnostic, device) pair, issued together so the client batches them into
      // ExecuteMultiCall requests, and a truncated page is paged forward instead of being cut
      // short.
      const collector = collectHistoricalStatusData(diagnosticIds, deviceIds);

      const tasks = diagnosticIds.flatMap(diagnosticId =>
        deviceIds.map(deviceId => ({
          key: historicalStatusDataKey(diagnosticId, deviceId),
          diagnosticId,
          deviceId,
        })),
      );

      await fetchStatusDataComplete(geotabClient, {
        tasks,
        fromDate,
        toDate: toDate ?? new Date(),
        onPage: collector.addPage,
        signal,
      });

      return collector.result;
    },

    refetchInterval: interval ?? 30000,
    placeholderData: keepPreviousData,
  };
}

/**
 * Returns historical status data for devices with diagnostic IDs within a specified time range.
 * @param diagnosticIds of the diagnostics we want to fetch
 * @param deviceIds of the devices, the data is needed
 * @param fromDate starting date of which the data is needed
 * @param interval for refetching data in ms, or false to disable polling (optional, defaults to 30 s)
 * @returns - Historical Status data for the devices with the dates when the status data was recorded
 */
export function useHistoricalStatusData(
  diagnosticIds: string[],
  deviceIds: string[],
  fromDate: Date,
  toDate?: Date,
  interval?: number | false,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  return useQuery(
    historicalStatusDataQuery(geotabClient, activeSessionId, diagnosticIds, deviceIds, fromDate, toDate, interval),
  );
}
